import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Undo2, ShieldAlert, PhoneCall, PauseCircle, CreditCard, Percent, IndianRupee, Flame, Shield } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import {
  AggHeader, PeriodSelect, ResetSampleButton, Kpi, Panel, useSort, SortTh, TH_ROW, TD, inr, inrCompact, num, pct,
  StatusPill, RiskPill, Pill, TableFilter, textMatch, FieldSelect,
} from "./aggKit";

function BreakdownTable({ title, rows, testid, nameLabel }) {
  const sort = useSort(rows, "rto_pct");
  const max = Math.max(1, ...rows.map((r) => r.rto_pct));
  return (
    <Panel title={title} testid={testid} bodyClass="overflow-x-auto max-h-[380px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className={`${TH_ROW} sticky top-0`}>
          <tr>
            <SortTh k="name" label={nameLabel} sort={sort} />
            <SortTh k="rto_pct" label="RTO %" sort={sort} />
            <SortTh k="rto" label="RTOs" sort={sort} align="right" />
            <SortTh k="high_risk_open" label="Hi-risk open" sort={sort} align="right" />
            <SortTh k="rto_cost" label="RTO cost" sort={sort} align="right" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sort.sorted.map((r) => (
            <tr key={r.key} className="hover:bg-muted/40">
              <td className={`${TD} max-w-[150px] truncate font-semibold`} title={r.name}>{r.name}</td>
              <td className={TD}>
                <div className="flex items-center gap-2 min-w-[110px]">
                  <div className="flex-1 h-1.5 bg-muted"><div className={`h-full ${r.rto_pct >= 25 ? "bg-rose-500" : r.rto_pct >= 15 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${(r.rto_pct / max) * 100}%` }} /></div>
                  <span className="font-mono-num w-10 text-right font-bold">{pct(r.rto_pct)}</span>
                </div>
              </td>
              <td className={`${TD} text-right font-mono-num`}>{r.rto}/{r.closed}</td>
              <td className={`${TD} text-right font-mono-num`}>{r.high_risk_open || "—"}</td>
              <td className={`${TD} text-right font-mono-num`}>{inrCompact(r.rto_cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

const ACTION_STYLE = {
  verified: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  hold: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  convert_prepaid: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
};

export default function AggRtoPage() {
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [level, setLevel] = useState("high");
  const [q, setQ] = useState("");
  const [actState, setActState] = useState("");

  const load = useCallback(() => {
    api.get("/agg/rto", { params: { days } }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [days]);
  useEffect(() => { load(); }, [load, tick]);

  const risky = useMemo(() => (d?.at_risk || []).filter((x) =>
    (!level || x.risk.level === level) &&
    (!actState || (actState === "none" ? !x.risk_action : x.risk_action?.action === actState)) &&
    textMatch(x, q, ["awb", "order_id", "merchant_name", "dest_city", "customer_name", "carrier_name"])), [d, level, q, actState]);
  const rs = useSort(risky, "risk_score");

  const act = async (x, action) => {
    if (action === "convert_prepaid" && !window.confirm(`Send a prepaid payment link to ${x.customer_name} and convert ${x.awb} from COD to prepaid?`)) return;
    try {
      await api.post(`/agg/shipments/${x.id}/risk-action`, { action });
      toast.success(action === "clear" ? "Action cleared" : "Action recorded");
      setTick((t) => t + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const o = d?.overall;
  const maxReason = Math.max(1, ...(d?.reasons || []).map((r) => r.count));

  return (
    <div className="space-y-4" data-testid="agg-rto-page">
      <AggHeader icon={ShieldAlert} title="RTO Optimizer"
        subtitle="Return-to-origin rates by merchant, region and carrier, plus open parcels likely to come back."
        actions={<><PeriodSelect value={days} onChange={setDays} testid="rto-period" /><ResetSampleButton onDone={() => setTick((t) => t + 1)} /></>} />
      {!d ? <div className="text-sm text-muted-foreground p-6">Loading RTO analytics…</div> : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <Kpi testid="rto-kpi-overall" label="Platform RTO rate" icon={Percent} accent="text-rose-500" value={pct(o.rto_pct)} sub={`${num(o.rto)} of ${num(o.closed)} closed parcels`} />
            <Kpi testid="rto-kpi-cod" label="COD vs prepaid RTO" icon={CreditCard} value={<span>{pct(o.cod_rto_pct)} <span className="text-base text-muted-foreground">/ {pct(o.prepaid_rto_pct)}</span></span>} sub="converting COD to prepaid cuts returns" />
            <Kpi testid="rto-kpi-cost" label="Reverse logistics cost" icon={IndianRupee} accent="text-orange-500" value={inrCompact(o.rto_cost)} sub="paid to 3PLs on returned parcels" />
            <Kpi testid="rto-kpi-risk" label="Likely returns in flight" icon={Flame} accent="text-rose-500" value={num(o.high_risk_open)} sub={`high risk · ${num(o.medium_risk_open)} medium`} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <BreakdownTable testid="rto-by-merchant" title="By merchant (MSME)" nameLabel="Merchant" rows={d.by_merchant} />
            <BreakdownTable testid="rto-by-region" title="By destination region" nameLabel="Region" rows={d.by_region} />
            <BreakdownTable testid="rto-by-carrier" title="By 3PL carrier" nameLabel="Carrier" rows={d.by_carrier} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
            <Panel testid="rto-reasons" title="Why parcels return" bodyClass="p-4 space-y-2">
              {d.reasons.map((r) => (
                <div key={r.reason} className="text-xs">
                  <div className="flex justify-between"><span>{r.reason}</span><span className="font-mono-num font-bold">{r.count}</span></div>
                  <div className="h-1.5 bg-muted mt-1"><div className="h-full bg-rose-500" style={{ width: `${(r.count / maxReason) * 100}%` }} /></div>
                </div>
              ))}
              <div className="border-t border-border pt-3 mt-3 text-[11px] text-muted-foreground space-y-1">
                <div className="font-bold uppercase tracking-wide text-foreground flex items-center gap-1"><Shield className="h-3 w-3" /> How the risk score works</div>
                <div>Merchant RTO history up to 35 pts, destination region up to 25, carrier up to 10, COD +15, high value +5 to +10, failed delivery attempts up to +10.</div>
                <div><b>60+</b> high · <b>40–59</b> medium.</div>
              </div>
            </Panel>
            <Panel className="xl:col-span-3" testid="rto-at-risk" title="Parcels likely to return"
              subtitle="Act before dispatch: verify by phone, hold, or convert COD to prepaid"
              actions={<>
                <FieldSelect testid="risk-level" value={level} onChange={setLevel} className="h-8 text-xs"><option value="high">High risk</option><option value="medium">Medium risk</option><option value="">All flagged</option></FieldSelect>
                <FieldSelect testid="risk-action-filter" value={actState} onChange={setActState} className="h-8 text-xs"><option value="">Any action</option><option value="none">No action yet</option><option value="verified">Verified</option><option value="hold">On hold</option><option value="convert_prepaid">Converted</option></FieldSelect>
                <TableFilter testid="risk-search" value={q} onChange={setQ} placeholder="Filter AWB, merchant, city…" />
              </>} bodyClass="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className={`${TH_ROW} sticky top-0 z-10`}>
                  <tr>
                    <SortTh k="awb" label="AWB" sort={rs} />
                    <SortTh k="merchant_name" label="Merchant" sort={rs} />
                    <SortTh k="dest_city" label="Destination" sort={rs} />
                    <SortTh k="carrier_name" label="Carrier" sort={rs} />
                    <SortTh k="declared_value" label="Value" sort={rs} align="right" />
                    <SortTh k="status" label="Stage" sort={rs} />
                    <SortTh k="risk_score" label="Risk" sort={rs} />
                    <th className="px-3 py-2 text-left">Why</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rs.sorted.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No flagged parcels.</td></tr>}
                  {rs.sorted.map((x) => (
                    <tr key={x.id} className="hover:bg-muted/40 align-top" data-testid={`risk-row-${x.id}`}>
                      <td className={TD}><div className="font-mono-num font-bold">{x.awb}</div><div className="text-[10px] text-muted-foreground">{x.customer_name}</div></td>
                      <td className={`${TD} max-w-[140px] truncate`}>{x.merchant_name}</td>
                      <td className={TD}>{x.dest_city}<div className="text-[10px] text-muted-foreground">{x.dest_region}</div></td>
                      <td className={TD}>{x.carrier_name}</td>
                      <td className={`${TD} text-right font-mono-num`}>{inr(x.declared_value)}<div className="text-[10px] uppercase text-muted-foreground">{x.payment_mode}</div></td>
                      <td className={TD}><StatusPill status={x.status} /></td>
                      <td className={TD}><RiskPill risk={x.risk} /></td>
                      <td className="px-3 py-2 text-[10px] text-muted-foreground min-w-[180px] max-w-[240px]">{x.risk.reasons.join(" · ")}</td>
                      <td className={TD}>
                        {x.risk_action ? (
                          <div className="flex items-center gap-1.5">
                            <Pill className={ACTION_STYLE[x.risk_action.action]}>{x.risk_action.label}</Pill>
                            <button title="Clear" onClick={() => act(x, "clear")} data-testid={`risk-clear-${x.id}`} className="h-6 w-6 grid place-items-center border border-border hover:bg-muted"><Undo2 className="h-3 w-3" /></button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <button title="Customer verified by call" onClick={() => act(x, "verified")} data-testid={`risk-verify-${x.id}`} className="h-6 px-1.5 border border-border hover:bg-muted text-[10px] font-bold uppercase inline-flex items-center gap-1"><PhoneCall className="h-3 w-3" /> Verify</button>
                            <button title="Hold dispatch" onClick={() => act(x, "hold")} data-testid={`risk-hold-${x.id}`} className="h-6 px-1.5 border border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 text-[10px] font-bold uppercase inline-flex items-center gap-1"><PauseCircle className="h-3 w-3" /> Hold</button>
                            {x.payment_mode === "cod" && <button title="Convert to prepaid" onClick={() => act(x, "convert_prepaid")} data-testid={`risk-prepaid-${x.id}`} className="h-6 px-1.5 border border-sky-500/40 text-sky-700 dark:text-sky-300 hover:bg-sky-500/10 text-[10px] font-bold uppercase inline-flex items-center gap-1"><CreditCard className="h-3 w-3" /> Prepaid</button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
